import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Lock,
  CheckCircle2,
  XCircle,
  Ban,
  Calendar,
  RotateCcw,
} from "lucide-react";
import ScoreBadge from "../common/ScoreBadge";
import JusticeIcon from "../common/JusticeIcon";
import WhatsAppIcon from "../common/WhatsAppIcon";
import CreditProductBadge from "./CreditProductBadge";
import { formatLoaLabel } from "../../utils/loaYear";
import { enrichedLeadService } from "../../services/enrichedLeadService";
import { useAuth } from "../../context/AuthContext";
import StartAgentModal from "../agent/StartAgentModal";
import { leadStatusActionClass } from "./leadStatusActionClass";

/** Status que bloqueiam de verdade o início do agente (não só "em contato"). */
function isTerminalContactStatus(status) {
  return ["PROPOSAL_SENT", "NEGOTIATING", "CONVERTED", "REJECTED"].includes(
    status,
  );
}

function isInContactStatus(status, assignedTo) {
  return (
    (status === "CONTACTED" ||
      status === "PROPOSAL_SENT" ||
      status === "NEGOTIATING") &&
    Boolean(assignedTo)
  );
}

function getViabilityBadge(viabilidade) {
  if (!viabilidade) return null;

  const map = {
    ALTA: "bg-green-100 text-green-700 border-green-200",
    MEDIA: "bg-yellow-100 text-yellow-700 border-yellow-200",
    BAIXA: "bg-orange-100 text-orange-700 border-orange-200",
    INCONCLUSIVO: "bg-slate-100 text-slate-700 border-slate-200",
    INVIAVEL: "bg-red-100 text-red-700 border-red-200",
  };

  return {
    label:
      viabilidade === "MEDIA"
        ? "Média"
        : viabilidade.charAt(0) + viabilidade.slice(1).toLowerCase(),
    className: map[viabilidade] || "bg-gray-100 text-gray-700 border-gray-200",
  };
}

function formatCurrency(value) {
  if (!value || value <= 0) return null;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function formatDateBR(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-BR");
}

function normalizeName(value) {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function findContactForParte(parte, contacts = []) {
  const parteName = normalizeName(parte.nome);
  const parteDocs = (parte.documentos || [])
    .map((doc) => doc.value?.replace(/\D/g, ""))
    .filter(Boolean);

  return contacts.find((contact) => {
    if (normalizeName(contact.name) === parteName) return true;
    const contactDoc = contact.document?.replace(/\D/g, "");
    return contactDoc && parteDocs.includes(contactDoc);
  });
}

export function listAgentContacts(lead) {
  const processData = lead?.processData;
  const contacts =
    lead?.contacts?.length > 0
      ? lead.contacts
      : lead?.id && lead?.name
        ? [
            {
              leadId: lead.id,
              name: lead.name,
              document: lead.document,
              phones: lead.phones || [],
              phone: lead.phone,
              status: lead.status,
              assignedTo: lead.assignedTo,
            },
          ]
        : [];
  const poloAtivo = processData?.poloAtivo || [];
  return poloAtivo
    .filter((parte) => normalizeName(parte.tipo) !== "advogado")
    .map((parte) => {
      const contact = findContactForParte(parte, contacts);
      const primaryPhone = getPrimaryPhone(contact);
      const terminalBusy = isTerminalContactStatus(contact?.status);
      const contactBlocked = enrichedLeadService.isContactBlocked(contact);
      return {
        parte,
        contact,
        primaryPhone,
        terminalBusy,
        contactBlocked,
        canStartAgent:
          !!(contact?.leadId && primaryPhone) && !terminalBusy && !contactBlocked,
        inContact: isInContactStatus(contact?.status, contact?.assignedTo),
        leadId: contact?.leadId,
        name: parte.nome || contact?.name || "",
      };
    });
}

function getPrimaryPhone(contact) {
  if (!contact) return "";
  if (typeof contact.phone === "string" && contact.phone.trim()) {
    return contact.phone.trim();
  }
  const phones = contact.phones || [];
  if (phones.length === 0) return "";
  const first = phones[0];
  if (typeof first === "string") return first;
  return first?.telefoneFormatado || first?.telefone || first?.number || "";
}

/** Nome do autor (polo ativo, nunca advogado). Fallback para contato/lead. */
function getAuthorName(processData, contactsList, lead) {
  const poloAtivo = processData?.poloAtivo || [];
  const autor = poloAtivo.find(
    (p) => normalizeName(p.tipo) !== "advogado",
  );
  if (autor?.nome) return autor.nome;
  if (poloAtivo[0]?.nome) return poloAtivo[0].nome;
  const contactName = contactsList.find((c) => c.name)?.name;
  if (contactName) return contactName;
  return lead?.name || "Autor não identificado";
}

/** Nome da empresa/parte do polo passivo (réu, nunca advogado). */
function getCompanyName(processData) {
  const poloPassivo = processData?.poloPassivo || [];
  const parte = poloPassivo.find(
    (p) => normalizeName(p.tipo) !== "advogado",
  );
  return parte?.nome || poloPassivo[0]?.nome || null;
}

export default function LeadCard({
  lead,
  onClick,
  onLeadUpdated,
  selectedContactIds,
  onToggleContact,
  onSelectLeadContacts,
}) {
  const { processData, contacts = [] } = lead;
  const contactsList = contacts.length > 0 ? contacts : (lead.id && lead.name ? [{
    leadId: lead.id,
    name: lead.name,
    document: lead.document,
    phones: lead.phones || [],
    status: lead.status,
    assignedTo: lead.assignedTo,
  }] : []);

  const navigate = useNavigate();
  const { user } = useAuth();
  const [agentLoadingLeadId, setAgentLoadingLeadId] = React.useState(null);
  const [startAgentContact, setStartAgentContact] = React.useState(null);
  const [statusUpdating, setStatusUpdating] = React.useState(false);
  const [clearingContactId, setClearingContactId] = React.useState(null);

  const processNumber = processData?.numeroProcesso || "";
  const formattedProcessNumber = processNumber
    ? enrichedLeadService.formatProcessNumber(processNumber)
    : null;

  const viabilityBadge = getViabilityBadge(processData?.viabilidade);
  const parecerText =
    processData?.parecer || processData?.recommendation || processData?.recomendacao || null;

  const authorName = getAuthorName(processData, contactsList, lead);
  const companyName = getCompanyName(processData);

  const loaLabel = formatLoaLabel(processData?.tipoProduto);
  const cardStatusMeta = enrichedLeadService.getStatusMeta(lead.status);
  const canMarkConverted = lead.status !== "CONVERTED";
  const canMarkLost = lead.status !== "LOST";
  const canMarkRejected = lead.status !== "REJECTED";
  const entryDateLabel = formatDateBR(lead.createdAt);

  async function handleCardStatusAction(e, action) {
    e.stopPropagation();
    if (statusUpdating) return;

    if (action === "CONVERTED") {
      if (!confirm("Marcar este lead como convertido?")) return;
    } else if (action === "LOST") {
      const reason = window.prompt("Motivo da perda:", lead.lostReason || "");
      if (reason === null) return;
      setStatusUpdating(true);
      try {
				await enrichedLeadService.applyStatusToCard(lead, "LOST", reason);
				if (onLeadUpdated) {
					onLeadUpdated({
						leadId: lead.id,
						processId: lead.processId || lead.id,
						action: "LOST",
					});
				}
      } catch (err) {
        alert(`Não foi possível atualizar o status: ${err?.message || "erro"}`);
      } finally {
        setStatusUpdating(false);
      }
      return;
    } else if (action === "REJECTED") {
      const reason = window.prompt(
        "Motivo da inaptidão (opcional):",
        lead.lostReason || "",
      );
      if (reason === null) return;
      setStatusUpdating(true);
      try {
        await enrichedLeadService.applyStatusToCard(lead, "REJECTED", reason);
        if (onLeadUpdated) {
          onLeadUpdated({
            leadId: lead.id,
            processId: lead.processId || lead.id,
            action: "REJECTED",
          });
        }
      } catch (err) {
        alert(`Não foi possível atualizar o status: ${err?.message || "erro"}`);
      } finally {
        setStatusUpdating(false);
      }
      return;
    }

    setStatusUpdating(true);
    try {
      await enrichedLeadService.applyStatusToCard(lead, action);
      if (onLeadUpdated) {
        onLeadUpdated({
          leadId: lead.id,
          processId: lead.processId || lead.id,
          action,
        });
      }
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || "erro"}`);
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleWhatsAppClick(e, contact) {
    e.stopPropagation();
    if (enrichedLeadService.isContactBlocked(contact)) {
      alert(
        "Contato bloqueado (parceiro ruim ou bloqueio manual). Não é permitido abrir WhatsApp.",
      );
      return;
    }
    const leadId = contact?.leadId;
    if (!leadId) return;
    // WhatsApp manual não é bloqueado por "em contato"; só evita marcar de novo se já busy.
    if (!enrichedLeadService.isContactBusy(contact?.status, contact?.assignedTo)) {
      try {
        await enrichedLeadService.markInContact(
          leadId,
          enrichedLeadService.resolveContactUserName(user),
        );
        if (onLeadUpdated) onLeadUpdated({ leadId: lead.id });
      } catch {
        /* não bloqueia abertura do WhatsApp */
      }
    }
  }

  async function handleClearInContact(e, contact) {
    e.stopPropagation();
    const leadId = contact?.leadId;
    if (!leadId || clearingContactId) return;
    const who = contact.assignedTo ? ` (${contact.assignedTo})` : "";
    if (
      !confirm(
        `Remover o status "Em contato"${who} e liberar este lead para novo atendimento?`,
      )
    ) {
      return;
    }
    setClearingContactId(leadId);
    try {
      await enrichedLeadService.reopenContact(leadId);
      if (onLeadUpdated) onLeadUpdated({ leadId: lead.id });
    } catch (err) {
      alert(
        `Não foi possível remover o status em contato: ${err?.message || "erro"}`,
      );
    } finally {
      setClearingContactId(null);
    }
  }

  const hasValues =
    processData &&
    (processData.score !== undefined ||
      viabilityBadge ||
      processData.valorEstimado > 0);

  const submittedByLabel =
    lead.submittedByName ||
    lead.submittedByEmail ||
    contactsList.find((c) => c.submittedByName || c.submittedByEmail)?.submittedByName ||
    contactsList.find((c) => c.submittedByName || c.submittedByEmail)?.submittedByEmail;

  function handleStartAgent(e, contact) {
    e.stopPropagation();
    if (enrichedLeadService.isContactBlocked(contact)) {
      alert(
        "Contato bloqueado (parceiro ruim ou bloqueio manual). Não é permitido iniciar o agente.",
      );
      return;
    }
    const primaryPhone = getPrimaryPhone(contact);
    const leadId = contact.leadId;

    if (!primaryPhone || !leadId) return;
    if (isTerminalContactStatus(contact.status)) {
      alert(
        "Este lead está em status avançado do funil e não pode iniciar o agente agora.",
      );
      return;
    }

    const inContact = isInContactStatus(contact.status, contact.assignedTo);
    if (inContact) {
      const who = contact.assignedTo ? ` por ${contact.assignedTo}` : "";
      if (
        !confirm(
          `Este contato já está em atendimento${who}. Iniciar o agente mesmo assim?`,
        )
      ) {
        return;
      }
    }

    setStartAgentContact({ ...contact, primaryPhone });
  }

  async function handleAgentStarted(sessionId, mode = "automatic") {
    const contact = startAgentContact;
    setStartAgentContact(null);
    if (!contact) return;
    try {
      await enrichedLeadService.markInContact(
        contact.leadId,
        enrichedLeadService.resolveContactUserName(user),
      );
    } catch {
      /* status é secundário ao início da conversa */
    }
    if (onLeadUpdated) onLeadUpdated({ leadId: lead.id });
    const label =
      mode === "manual"
        ? "Atendimento manual iniciado! Abrir conversa agora para enviar a primeira mensagem?"
        : "Agente automático iniciado! Abrir conversa agora?";
    if (confirm(label)) {
      navigate(`/atendimento/conversas?session=${sessionId}`);
    }
  }

  const poloAtivo = processData?.poloAtivo || [];

  return (
    <div
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer border border-gray-200"
      onClick={() => onClick && onClick(lead)}
    >
      {/* Header: autor em destaque + empresa (réu) ao lado */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-xl text-gray-900 truncate">
                {authorName}
              </h3>
              {companyName && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  {companyName}
                </span>
              )}
              <CreditProductBadge tipoProduto={processData?.tipoProduto} />
              {loaLabel && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200"
                  title="Ano estimado da Lei Orçamentária Anual"
                >
                  {loaLabel}
                </span>
              )}
              {cardStatusMeta && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${cardStatusMeta.className}`}
                >
                  {cardStatusMeta.key === "CONTACTED" && lead.assignedTo
                    ? `Em contato • ${lead.assignedTo}`
                    : cardStatusMeta.label}
                  {(cardStatusMeta.key === "LOST" || cardStatusMeta.key === "REJECTED") &&
                  lead.lostReason
                    ? ` • ${lead.lostReason}`
                    : ""}
                </span>
              )}
            </div>
            {formattedProcessNumber && (
              <p className="text-sm text-gray-600 font-mono mt-1">
                {formattedProcessNumber}
              </p>
            )}
            {entryDateLabel && (
              <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Entrada: {entryDateLabel}
              </p>
            )}
            {processData?.vara && (
              <p className="text-xs text-gray-500 mt-1">📍 {processData.vara}</p>
            )}
            {submittedByLabel && (
              <p className="text-xs text-gray-500 mt-1">
                Enviado por:{" "}
                <span className="font-medium text-gray-700">{submittedByLabel}</span>
              </p>
            )}
          </div>

          <div
            className="flex flex-wrap items-center justify-end gap-1.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {canMarkConverted && (
              <button
                type="button"
                onClick={(e) => handleCardStatusAction(e, "CONVERTED")}
                disabled={statusUpdating}
                className={leadStatusActionClass.converted}
                title="Marcar como convertido"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Convertido
              </button>
            )}
            {canMarkLost && (
              <button
                type="button"
                onClick={(e) => handleCardStatusAction(e, "LOST")}
                disabled={statusUpdating}
                className={leadStatusActionClass.lost}
                title="Marcar como perdido"
              >
                <XCircle className="w-3.5 h-3.5" />
                Perdido
              </button>
            )}
            {canMarkRejected && (
              <button
                type="button"
                onClick={(e) => handleCardStatusAction(e, "REJECTED")}
                disabled={statusUpdating}
                className={leadStatusActionClass.rejected}
                title="Marcar como inapto"
              >
                <Ban className="w-3.5 h-3.5" />
                Inapto
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cálculos e valores em destaque, antes dos detalhes */}
      {hasValues && (
        <div className="px-6 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-900 mr-1">
                Cálculos e valores
              </p>
              {processData?.score !== undefined && (
                <ScoreBadge score={processData.score} />
              )}
              {viabilityBadge && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${viabilityBadge.className}`}
                >
                  Viabilidade {viabilityBadge.label}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-gray-500 mb-0.5">Valor estimado</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(processData?.valorEstimado) || "—"}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Homologado / resolvido do processo
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {processData && (
              <div className="flex items-center space-x-3">
                <JusticeIcon type={processData.esfera} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {processData.tribunal || "Tribunal não informado"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {processData.estado || processData.provinceName || "Estado não informado"}
                    {processData.cityName && ` - ${processData.cityName}`}
                    {" • "}
                    {processData.justiceTypeName || processData.esfera || "N/A"}
                  </p>
                </div>
              </div>
            )}

            {parecerText && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">
                  Parecer
                </p>
                <p className="text-sm text-gray-800 line-clamp-4 leading-relaxed">
                  {parecerText}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {processData?.blockers && processData.blockers.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-semibold">Impedimentos</p>
                <div className="space-y-1">
                  {processData.blockers.slice(0, 2).map((blocker, idx) => (
                    <div
                      key={idx}
                      className="text-sm bg-red-50 border border-red-200 rounded px-3 py-2 text-red-800"
                    >
                      {blocker}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {poloAtivo.length > 0 && (
              <div>
                {(() => {
                  const agentRows = listAgentContacts(lead).filter(
                    (row) => row.canStartAgent && row.leadId,
                  );
                  const allSelected =
                    agentRows.length > 0 &&
                    agentRows.every((row) => selectedContactIds?.has(row.leadId));
                  return (
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs text-gray-500 font-semibold">Polo Ativo (Autor)</p>
                      {agentRows.length > 1 && onSelectLeadContacts && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectLeadContacts(agentRows, !allSelected);
                          }}
                          className="shrink-0 px-2.5 py-1 text-xs font-semibold text-emerald-800 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50"
                        >
                          {allSelected ? "Limpar seleção" : "Selecionar todos"}
                        </button>
                      )}
                    </div>
                  );
                })()}
                <div className="space-y-3">
                  {listAgentContacts(lead).map((row, idx) => {
                    const { parte, contact } = row;
                    const phones = contact?.phones || [];
                    const primaryPhone = row.primaryPhone;
                    const inContact = row.inContact;
                    const terminalBusy = row.terminalBusy;
                    const contactBlocked = row.contactBlocked;
                    const canStartAgent = row.canStartAgent;
                    const selected = Boolean(selectedContactIds?.has(contact?.leadId));
                    const isLoading = agentLoadingLeadId === contact?.leadId;
                    const isClearing = clearingContactId === contact?.leadId;
                    const statusMeta = enrichedLeadService.getStatusMeta(contact?.status);

                    return (
                      <div
                        key={`${parte.nome}-${idx}`}
                        className={`rounded-lg border px-3 py-3 space-y-2 ${
                          contactBlocked
                            ? "border-red-200 bg-red-50"
                            : "border-green-200 bg-green-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          {canStartAgent && onToggleContact && (
                            <input
                              type="checkbox"
                              checked={selected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                onToggleContact(
                                  {
                                    leadId: contact.leadId,
                                    name: parte.nome || contact.name,
                                    primaryPhone,
                                    inContact,
                                  },
                                  e.target.checked,
                                );
                              }}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              title="Selecionar para envio em lote"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">{parte.nome}</div>
                            <div className="text-xs text-gray-600">{parte.tipo}</div>
                            {contactBlocked && (
                              <div className="text-xs text-red-700 font-medium mt-0.5">
                                Contato bloqueado
                              </div>
                            )}
                          </div>
                          {statusMeta && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${statusMeta.className}`}
                            >
                              {statusMeta.key === "CONTACTED" && contact?.assignedTo
                                ? `Em contato • ${contact.assignedTo}`
                                : statusMeta.label}
                            </span>
                          )}
                        </div>

                        {phones.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {phones.slice(0, 2).map((phone, phoneIdx) =>
                              contactBlocked ? (
                                <span
                                  key={phoneIdx}
                                  className="inline-flex items-center space-x-2 px-2.5 py-1.5 bg-gray-300 text-gray-600 rounded-md text-xs font-medium cursor-not-allowed"
                                  title="Contato bloqueado"
                                >
                                  <span>{phone.telefoneFormatado}</span>
                                  <span>Bloqueado</span>
                                </span>
                              ) : (
                                <a
                                  key={phoneIdx}
                                  href={phone.whatsappLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => handleWhatsAppClick(e, contact)}
                                  className="inline-flex items-center space-x-2 px-2.5 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md text-xs font-medium transition-colors shadow-sm"
                                  title={`${phone.tipoTelefone || "WhatsApp"} - ${phone.operadora || ""}`}
                                >
                                  <WhatsAppIcon className="w-3.5 h-3.5" />
                                  <span>{phone.telefoneFormatado}</span>
                                  {phone.qualificacao === 1 && (
                                    <span className="text-xs font-bold" title="Verificado">✓</span>
                                  )}
                                </a>
                              ),
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Sem telefone para esta parte</p>
                        )}

                        {contact?.leadId && (
                          <div className="flex flex-wrap justify-end gap-2">
                            {inContact && (
                              <button
                                type="button"
                                onClick={(e) => handleClearInContact(e, contact)}
                                disabled={isClearing || statusUpdating}
                                title="Remove o status Em contato e libera o lead"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-gray-50 disabled:opacity-50 border border-gray-300 text-gray-700 text-xs font-medium rounded-md transition"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {isClearing ? "Removendo..." : "Remover em contato"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleStartAgent(e, contact)}
                              disabled={!canStartAgent || isLoading}
                              title={
                                contactBlocked
                                  ? "Contato bloqueado"
                                  : terminalBusy
                                    ? "Status do funil impede novo início do agente"
                                    : inContact
                                      ? contact?.assignedTo
                                        ? `Já em contato por ${contact.assignedTo} — clique para iniciar mesmo assim`
                                        : "Já em contato — clique para iniciar mesmo assim"
                                      : !primaryPhone
                                        ? "Sem telefone cadastrado para esta parte"
                                        : "Iniciar agente via WhatsApp com esta parte"
                              }
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition"
                            >
                              {terminalBusy || contactBlocked ? (
                                <Lock className="w-3.5 h-3.5" />
                              ) : (
                                <MessageSquare className="w-3.5 h-3.5" />
                              )}
                              {isLoading
                                ? "Iniciando..."
                                : contactBlocked
                                  ? "Bloqueado"
                                  : inContact
                                    ? "Iniciar mesmo assim"
                                    : "Iniciar agente"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {processData?.poloPassivo && processData.poloPassivo.length > 0 && (
                  <div className="mt-3 border-l-2 border-red-100 pl-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600 mb-1">
                      Polo Passivo (Réu)
                    </p>
                    <div className="space-y-1.5">
                      {processData.poloPassivo.slice(0, 2).map((parte, idx) => (
                        <div key={idx} className="text-xs text-gray-700">
                          <span className="font-medium text-gray-900">{parte.nome}</span>
                          <span className="text-gray-500 ml-1">• {parte.tipo}</span>
                        </div>
                      ))}
                      {processData.poloPassivo.length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{processData.poloPassivo.length - 2} parte(s) no polo passivo
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {startAgentContact && (
        <StartAgentModal
          contact={startAgentContact}
          onClose={() => setStartAgentContact(null)}
          onStarted={handleAgentStarted}
        />
      )}
    </div>
  );
}